import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `<AccountHomeTabNavigation />` — 5-tab navigation state machine for
 * `<AccountHomeView />` per CPL Smart Health Account dashboard UX v0.2
 * §3 records-first IA + UC1 Patient Access Sprint 9 execution scope.
 *
 * **Sprint 9 IMPL scope (this scaffold):**
 *   - 5 tabs canonical: Records / Your healthcare team / Activity / Care / Settings
 *   - WAI-ARIA `role="tablist"` + `role="tab"` + `role="tabpanel"` correct pattern
 *   - Keyboard navigation: Left/Right/Home/End arrow keys move active tab focus;
 *     Tab key moves focus from tablist to active tabpanel
 *   - Tab selection state controlled (parent owns; this component is presentation-
 *     only state machine over the controlled selection)
 *
 * **Sub-scopes substrate-state-event-paced (NOT in this scaffold):**
 *   - Records list rendering (DEV5 + DEV3 wire-contract + Pattern B client-side
 *     rendering; CPL §3.1 canonical)
 *   - Healthcare team rendering (DEV5 + ADR-033 substrate-fiduciary-managed
 *     handle multi-role primitive; CPL §3.2 canonical)
 *   - Activity rendering (embeds `<OpsLogContainer />` when integration prop
 *     provided; CPL §3.3 canonical; PR #586 wireup preserved)
 *   - Care rendering (DEV5 + appointment data source substrate-state-event-paced;
 *     CPL §3.4 canonical; Smart Health Secure Messaging Sprint 9+ chained)
 *   - Settings rendering (DEV5 + existing D2/D3 surface composition; CPL §3.5
 *     canonical)
 *
 * # Doctrinal anchors preserved
 *
 *   - Recovery UX honesty (`feedback_phg_recovery_ux_honesty.md`) — placeholder
 *     tab content preserves "substantive content substrate-state-event-paced"
 *     framing without false-success rendering
 *   - Pattern B no-parallel-rendering-surface — client-side React state machine;
 *     no server-rendered tab state
 *   - Active-session-only crypto — tab navigation is structural metadata only;
 *     no cleartext key material at this surface
 *
 * # CPL UX v0.2 §3 mapping (this scaffold satisfies records-first IA structural
 *   contract; visual treatment composes substrate-state-event-paced via MSE
 *   Workstream D Web Components Brand Guide v1 migration cycle)
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
export const ACCOUNT_HOME_TABS = [
    'records',
    'healthcare_team',
    'activity',
    'care',
    'settings',
];
export function AccountHomeTabNavigation(props) {
    const { t } = useLocale();
    const { activeTab, onTabChange } = props;
    const tabRefs = React.useRef({
        records: null,
        healthcare_team: null,
        activity: null,
        care: null,
        settings: null,
    });
    const focusTab = React.useCallback((tab) => {
        const el = tabRefs.current[tab];
        if (el !== null) {
            el.focus();
        }
    }, []);
    const handleKeyDown = React.useCallback((event) => {
        const currentIndex = ACCOUNT_HOME_TABS.indexOf(activeTab);
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            const nextTab = ACCOUNT_HOME_TABS[(currentIndex + 1) % ACCOUNT_HOME_TABS.length];
            onTabChange(nextTab);
            focusTab(nextTab);
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            const prevTab = ACCOUNT_HOME_TABS[(currentIndex - 1 + ACCOUNT_HOME_TABS.length) % ACCOUNT_HOME_TABS.length];
            onTabChange(prevTab);
            focusTab(prevTab);
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            const firstTab = ACCOUNT_HOME_TABS[0];
            onTabChange(firstTab);
            focusTab(firstTab);
        }
        else if (event.key === 'End') {
            event.preventDefault();
            const lastTab = ACCOUNT_HOME_TABS[ACCOUNT_HOME_TABS.length - 1];
            onTabChange(lastTab);
            focusTab(lastTab);
        }
    }, [activeTab, onTabChange, focusTab]);
    return (_jsx("div", { role: "tablist", "aria-label": t('account.home.tabs.label'), className: "account-home-tablist", "data-testid": "account-home-tablist", children: ACCOUNT_HOME_TABS.map((tab) => {
            const isActive = tab === activeTab;
            const labelKey = `account.home.tabs.${tab}.label`;
            return (_jsx("button", { ref: (el) => {
                    tabRefs.current[tab] = el;
                }, type: "button", role: "tab", "aria-selected": isActive, "aria-controls": `account-home-tabpanel-${tab}`, id: `account-home-tab-${tab}`, tabIndex: isActive ? 0 : -1, "data-testid": `account-home-tab-${tab}`, className: isActive
                    ? 'account-home-tab account-home-tab--active'
                    : 'account-home-tab', onClick: () => onTabChange(tab), onKeyDown: handleKeyDown, children: t(labelKey) }, tab));
        }) }));
}
//# sourceMappingURL=tab-navigation.js.map